import { z } from 'zod';
import { visuelSchema } from './common.js';
/**
 * Le SITE VITRINE d'une boutique.
 *
 * ⭐ Une structure, pas une page. Le gérant ajoute, réordonne et masque des
 * sections ; le gabarit ne fait que les dessiner. Un site figé en dur aurait
 * obligé à toucher au code pour déplacer un bloc — c'est-à-dire à faire passer
 * chaque enseigne par nous.
 *
 * ⚠️ Deux volontés séparées, et il faut qu'elles le restent : le CONTENU
 * appartient au gérant, la MISE EN LIGNE à l'administrateur de la plateforme.
 * Ce schéma ne décrit que le premier — le statut de publication n'y figure pas,
 * pour qu'aucun enregistrement de brouillon ne puisse publier un site.
 */
const texteCourt = z.string().trim().max(160);
const texteLong = z.string().trim().max(4000);
/** Une ancre interne (« #services ») ou une adresse complète. */
const lienSchema = z.object({
    label: texteCourt,
    href: z.string().trim().max(300),
});
const boutonSchema = lienSchema.extend({
    /** `rendezvous` ouvre la prise de rendez-vous en ligne au lieu de naviguer. */
    action: z.enum(['lien', 'rendezvous']).default('lien'),
});
/**
 * Les blocs disponibles.
 *
 * ⚠️ Chaque section porte son `id` : c'est lui qui sert de clé de rendu et
 * d'ancre de navigation. Se reposer sur l'indice aurait fait sauter les liens
 * du menu au premier déplacement de bloc.
 */
const baseSection = {
    id: z.string().trim().min(1).max(40),
    visible: z.boolean().default(true),
    titre: texteCourt.default(''),
    sousTitre: texteCourt.default(''),
};
export const sectionSchema = z.discriminatedUnion('type', [
    z.object({
        ...baseSection,
        type: z.literal('texte'),
        corps: texteLong.default(''),
        image: visuelSchema.nullish(),
        /** L'image à gauche ou à droite — le gérant alterne pour rythmer la page. */
        imageADroite: z.boolean().default(true),
    }),
    /**
     * ⭐ Ce qui paraît ici est décidé ARTICLE PAR ARTICLE, par la case « visible
     * sur le site » du catalogue. Une seconde liste recopiée dans la structure du
     * site aurait divergé du catalogue au premier article retiré, et il aurait
     * fallu penser à la corriger à deux endroits.
     */
    z.object({
        ...baseSection,
        type: z.literal('prestations'),
        afficherPrix: z.boolean().default(true),
        afficherDuree: z.boolean().default(true),
    }),
    z.object({
        ...baseSection,
        type: z.literal('produits'),
        afficherPrix: z.boolean().default(true),
    }),
    z.object({
        ...baseSection,
        type: z.literal('galerie'),
        /**
         * Images ET vidéos, déposées sur l'appareil OU liées ailleurs.
         *
         * ⭐ Un commerçant a souvent déjà ses visuels quelque part — une vidéo sur
         * une plateforme d'hébergement, des photos sur son nuage. Exiger un dépôt
         * l'obligerait à tout retélécharger, et une vidéo de démonstration pèserait
         * cent fois le poids raisonnable d'un document JSON.
         *
         * ⚠️ Le lien n'est PAS validé au-delà de sa forme : on ne va pas chercher
         * la ressource. Un lien mort affiche un cadre vide — c'est visible, et cela
         * se corrige ; refuser l'enregistrement parce qu'un serveur distant répond
         * mal serait pire.
         */
        medias: z
            .array(z.object({
            type: z.enum(['image', 'video']).default('image'),
            url: z.string().trim().min(1).max(400_000),
            legende: texteCourt.default(''),
        }))
            .max(24)
            .default([]),
    }),
    z.object({
        ...baseSection,
        type: z.literal('temoignages'),
        avis: z
            .array(z.object({ texte: texteLong, auteur: texteCourt }))
            .max(12)
            .default([]),
    }),
    z.object({
        ...baseSection,
        type: z.literal('horaires'),
        /** Les horaires viennent des réglages : on ne les recopie pas ici. */
        note: texteCourt.default(''),
    }),
    z.object({
        ...baseSection,
        type: z.literal('contact'),
        afficherAdresse: z.boolean().default(true),
        afficherTelephone: z.boolean().default(true),
        afficherEmail: z.boolean().default(true),
        note: texteLong.default(''),
    }),
    z.object({
        ...baseSection,
        type: z.literal('rendezvous'),
        intro: texteLong.default(''),
    }),
]);
export const TYPES_SECTION = [
    { type: 'texte', label: 'Texte & image', aide: 'Votre histoire, un engagement, une annonce.' },
    { type: 'prestations', label: 'Prestations', aide: 'Vos services, avec prix et durée.' },
    { type: 'produits', label: 'Produits', aide: 'Une sélection d’articles du catalogue.' },
    { type: 'galerie', label: 'Galerie', aide: 'Quelques photos de la boutique.' },
    { type: 'temoignages', label: 'Témoignages', aide: 'Ce que disent vos clients.' },
    { type: 'horaires', label: 'Horaires', aide: 'Repris de Paramètres › Agenda.' },
    { type: 'contact', label: 'Contact', aide: 'Adresse, téléphone, courriel.' },
    { type: 'rendezvous', label: 'Prendre rendez-vous', aide: 'Le formulaire en ligne.' },
];
export const siteSchema = z.object({
    modele: z.enum(['vitrine-1']).default('vitrine-1'),
    theme: z
        .object({
        couleur: z
            .string()
            .trim()
            .regex(/^#[0-9a-fA-F]{6}$/, 'Couleur hexadécimale attendue')
            .default('#2563eb'),
        /** Fond clair ou sombre — le reste du gabarit s'y accorde. */
        ambiance: z.enum(['clair', 'sombre']).default('clair'),
    })
        .prefault({}),
    entete: z
        .object({
        titre: texteCourt.default(''),
        logo: visuelSchema.nullish(),
        nav: z.array(lienSchema).max(8).default([]),
        cta: boutonSchema.nullish(),
    })
        .prefault({}),
    hero: z
        .object({
        titre: texteCourt.default(''),
        accroche: texteLong.default(''),
        /**
         * Les images de FOND du bandeau d'accueil.
         *
         * ⭐ Elles occupent toute la surface, texte par-dessus : c'est ce que fait
         * n'importe quelle vitrine moderne, et c'est ce qui donne son atmosphère à
         * la page. Une image posée à côté du texte ressemble à une illustration
         * d'article, pas à une devanture.
         *
         * ⭐ Plusieurs images défilent toutes seules — le commerçant montre son
         * salon, son équipe, une réalisation, sans que le visiteur ait à cliquer.
         *
         * ⚠️ Un lien externe est accepté au même titre qu'un fichier déposé : le
         * commerçant a souvent déjà ses visuels quelque part, et une photo pleine
         * page pèse lourd dans un document JSON.
         */
        images: z.array(z.string().trim().min(1).max(400_000)).max(8).default([]),
        /**
         * ⚠️ L'ANCIENNE image unique, conservée en lecture.
         *
         * Les sites écrits avant le bandeau plein cadre la portent encore. La
         * retirer du schéma aurait effacé silencieusement leur visuel au premier
         * enregistrement — le gabarit la reprend quand `images` est vide.
         */
        image: visuelSchema.nullish(),
        boutonPrincipal: boutonSchema.nullish(),
        boutonSecondaire: boutonSchema.nullish(),
    })
        .prefault({}),
    sections: z.array(sectionSchema).max(20).default([]),
    pied: z
        .object({
        texte: texteLong.default(''),
        liens: z.array(lienSchema).max(8).default([]),
    })
        .prefault({}),
});
/** Ce que le gérant peut modifier, et lui seul. */
export const majSiteSchema = z.object({
    contenu: siteSchema,
    rdvEnLigne: z.boolean().optional(),
});
/**
 * Un site de départ, prérempli avec ce que la plateforme sait déjà.
 *
 * ⭐ Jamais une page blanche : un gérant devant un site vide ne sait pas par
 * quel bout le prendre, et l'abandonne. Il corrige un texte, il n'en invente
 * pas un.
 */
export function siteParDefaut(boutique) {
    return siteSchema.parse({
        modele: 'vitrine-1',
        theme: { couleur: '#2563eb', ambiance: 'clair' },
        entete: {
            titre: boutique.nom,
            nav: [
                { label: 'Prestations', href: '#prestations' },
                { label: 'La boutique', href: '#apropos' },
                { label: 'Horaires', href: '#horaires' },
                { label: 'Contact', href: '#contact' },
            ],
            cta: { label: 'Prendre rendez-vous', href: '#rendezvous', action: 'rendezvous' },
        },
        hero: {
            titre: `Bienvenue chez ${boutique.nom}`,
            accroche: 'Décrivez en une phrase ce que vous faites de mieux. C’est la première chose que lira un visiteur.',
            boutonPrincipal: { label: 'Prendre rendez-vous', href: '#rendezvous', action: 'rendezvous' },
            boutonSecondaire: { label: 'Voir les prestations', href: '#prestations', action: 'lien' },
        },
        sections: [
            {
                id: 'prestations',
                type: 'prestations',
                titre: 'Nos prestations',
                sousTitre: 'Ce que nous proposons',
            },
            {
                id: 'apropos',
                type: 'texte',
                titre: 'La boutique',
                corps: 'Racontez votre histoire en quelques lignes : depuis quand vous exercez, ce qui vous distingue, qui vous accueille.',
            },
            { id: 'horaires', type: 'horaires', titre: 'Horaires d’ouverture' },
            {
                id: 'contact',
                type: 'contact',
                titre: 'Nous trouver',
                ...(boutique.adresse ? {} : {}),
            },
            {
                id: 'rendezvous',
                type: 'rendezvous',
                titre: 'Prendre rendez-vous',
                intro: 'Munissez-vous de votre référence client, indiquée sur votre carte de fidélité ou votre ticket.',
            },
        ],
        pied: { texte: `© ${new Date().getFullYear()} ${boutique.nom}` },
    });
}
// ------------------------------------------------- prise de rendez-vous en ligne
/**
 * L'identification d'un client sur le site public.
 *
 * ⚠️ La référence SEULE ne prouve rien : elle est imprimée sur la carte de
 * fidélité et sur les tickets, et se devine par simple incrément. On y adjoint
 * le NOM, que le porteur légitime connaît toujours et qu'un curieux doit
 * deviner en plus. Sans ce second élément, n'importe qui pourrait poser des
 * rendez-vous au nom d'autrui, et découvrir au passage quelles références
 * existent.
 *
 * Ce n'est pas un mot de passe, et cela n'en tient pas lieu — c'est le minimum
 * qui rende l'abus fastidieux plutôt qu'immédiat. Le serveur ne renvoie JAMAIS
 * la fiche du client, seulement « reconnu » ou « non reconnu ».
 */
export const identiteEnLigneSchema = z.object({
    reference: z.string().trim().min(3).max(40),
    nom: z.string().trim().min(2).max(80),
});
export const rdvEnLigneSchema = identiteEnLigneSchema.extend({
    itemId: z.string().uuid(),
    posteId: z.string().uuid().optional(),
    /**
     * La place choisie, par son RANG dans la grille (0 = « Poste 1 »).
     *
     * ⭐ Le site public ne montre jamais les identifiants de poste — seulement
     * des numéros de colonne. Le visiteur désigne pourtant une case précise : lui
     * en attribuer une autre en silence ferait mentir l'écran sur lequel il vient
     * de cliquer.
     *
     * ⚠️ Facultatif : une demande venue d'ailleurs, ou d'une catégorie sans
     * poste, laisse le serveur choisir comme avant.
     */
    posteIndex: z.coerce.number().int().min(0).max(29).optional(),
    startsAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=site.js.map